/* eslint-disable */

import React, { useState, useEffect } from 'react'
import {
  TextField,
  Grid,
  Typography,
  Button,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
} from '@mui/material'
import AddNotesDialog from './components/AddNotesDialog'
import AssignAgentDialog from './components/AssignAgentDialog'
import ViewApprovalTerms from './components/ViewApprovalTerms'
import AssociateVehiclesDialog from './components/AssociateVehiclesDialog'
import ViewDocumentsDialog from './components/ViewDocumentsDialog'
import AdminPandadocDialog from 'components/AdminPandadocDialog'
import { AdminPageFrame } from 'components/AdminPageFrame'
import { FilterToolbar } from 'components/FilterToolbar'
import { MobileRecordList } from 'components/MobileRecordList'
import { useIsMobile } from 'hooks/screen/useIsMobile'
import { Application } from '@otw/models'
import { useManageUsers } from 'hooks/admin/useManageUsers'
import ApplicationCard from './components/ApplicationCard'
import { useAdminApplications } from 'hooks/admin/useAdminApplications'
import { FreeTechLogoLoading } from 'components/FreeTechLogoLoading'
import { useSnackbar } from 'hooks/common/useSnackbar'
import {
  ApplicationStatusTimeline,
  ApplicationStatusFilter,
} from 'components/ApplicationStatusTimeline'
import { pandaFunctions } from 'core/functions/pandaFunctions'
import { usePermissions } from 'hooks/auth/usePermissions'
import PermissionDenied from 'components/PermissionDenied'

const ApplicationManagement = () => {
  const [filteredApps, setFilteredApps] = useState<Application[]>([])
  const [statusFilter, setStatusFilter] = useState<ApplicationStatusFilter>('signing')
  const [loadingDialogOpen, setLoadingDialogOpen] = useState(false)
  const [errorDialogOpen, setErrorDialogOpen] = useState(false)
  const [documentUrl, setDocumentUrl] = useState<string | null>(null)
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const [isAssignAgentDialogOpen, setAssignAgentDialog] = useState(false)
  const [selectedBankId, setSelectedBankId] = useState<string>('')
  const [selectedBankName, setSelectedBankName] = useState<string>('')
  const [selectedAgentId, setSelectedAgentId] = useState<string>('')
  const [selectedApplicationId, setSelectedApplicationId] = useState<string | null>(null)
  const [isAddNotesDialogOpen, setAddNotesDialogOpen] = useState(false)
  const [selectedApplication, setSelectedApplication] = useState<Application | null>(null)
  const [correctioNotes, setCorrectionNotes] = useState('')
  const [searchQuery, setSearchQuery] = useState('')
  const { checkPermission } = usePermissions()
  const hasViewPermission = checkPermission('applications', 'view')

  const isMobile = useIsMobile()
  const { showError } = useSnackbar()

  const { editApplication, applications, applicationsIsLoading } = useAdminApplications()

  const { authUsers } = useManageUsers()

  useEffect(() => {
    if (!applications) return

    const filtered = applications.filter((app: Application) => {
      const query = (searchQuery || '').toLowerCase()
      const matchesSearchQuery =
        (app.status?.toLowerCase() || '').includes(query) ||
        (app.bank_id?.toString()?.toLowerCase() || '').includes(query) ||
        (app.user_name?.toLowerCase() || '').includes(query) ||
        (app.time_created?.toLowerCase() || '').includes(query) ||
        (app.notes?.toLowerCase() || '').includes(query) ||
        (app.bank_decision_notes?.toLowerCase() || '').includes(query) ||
        (app.id?.toString() || '').includes(query) ||
        (app.bank_agent_id?.toLowerCase() || '').includes(query)

      const matchesStatus = () => {
        switch (statusFilter) {
          case 'signing':
            return app.status === 'signing'
          case 'ready':
            return app.status === 'ready'
          case 'corrections_needed':
            return app.status === 'corrections_needed'
          case 'pending':
            return app.status === 'pending'
          case 'viewed':
            return app.status === 'viewed'
          case 'approved':
            return app.status === 'approved' && app.completed !== true
          case 'denied':
            return app.status === 'denied' && !app.completed
          case 'completed':
            return app.completed === true
          default:
            return false
        }
      }

      return matchesSearchQuery && matchesStatus()
    })

    setFilteredApps(filtered)
  }, [statusFilter, applications, searchQuery])

  const handleCloseDialog = () => {
    setIsDialogOpen(false)
    setDocumentUrl(null)
    setSelectedApplication(null)
  }

  const handleCloseErrorDialog = () => {
    setErrorDialogOpen(false)
  }

  const handleAssignAgent = () => {
    if (!selectedApplicationId) return
    assignAgent(selectedApplicationId, selectedAgentId)
    setAssignAgentDialog(false)
  }

  const assignAgent = async (application_id: string, agent_id: string) => {
    try {
      const appToUpdate = applications?.find(app => app.id === application_id)
      if (!appToUpdate) return

      await editApplication.mutateAsync({
        ...appToUpdate,
        bank_agent_id: agent_id,
        status: 'pending',
      })
    } catch (error) {
      console.error('Error during agent assignment:', error)
    }
  }

  const openAssignAgentDialog = (application: Application) => {
    setSelectedApplicationId(application.id)
    setSelectedBankId(application.bank_id)
    setSelectedBankName(application.bank_id)
    setAssignAgentDialog(true)
  }

  const markApplicationCompleted = async (application_id: string) => {
    const appToUpdate = applications?.find(app => app.id === application_id)
    if (!appToUpdate) return

    await editApplication.mutateAsync({
      ...appToUpdate,
      completed: true,
    })
  }

  const handleOpenAddNotesDialog = (application: Application) => {
    setSelectedApplication(application)
    setAddNotesDialogOpen(true)
  }

  const handleCloseAddNotesDialog = () => {
    setAddNotesDialogOpen(false)
  }

  const handleViewDocument = async (application: Application) => {
    if (application.status === 'signing') {
      showError('Document is currently being signed and cannot be viewed')
      return
    }

    setLoadingDialogOpen(true)
    try {
      const url = await pandaFunctions.shared.getViewableDocument(application.id)
      console.log('url', url)

      setSelectedApplication(application)
      console.log('application', application)

      if (!url) {
        throw new Error('No document URL found')
      }
      setDocumentUrl(url)
      setLoadingDialogOpen(false)
      setIsDialogOpen(true)
    } catch (error) {
      console.error('Error fetching document:', error)
      setErrorDialogOpen(true)
      setLoadingDialogOpen(false)
    }
  }

  const handleSendDocumentBackForCorrections = async (application_id: string, correction_notes: string) => {
    try {
      const appToUpdate = applications?.find(app => app.id === application_id)
      if (!appToUpdate) return

      await editApplication.mutateAsync({
        ...appToUpdate,
        status: 'corrections_needed',
        correction_notes: correction_notes,
      })
      setDocumentUrl(null)
    } catch (error) {
      setErrorDialogOpen(true)
      setLoadingDialogOpen(false)
    }
  }

  const LoadingDialog = () => (
    <Dialog open={loadingDialogOpen} onClose={handleCloseLoadingDialog} fullScreen={isMobile}>
      <DialogTitle>Loading Document..</DialogTitle>
      <DialogContent>
        <CircularProgress />
      </DialogContent>
    </Dialog>
  )

  const handleCloseLoadingDialog = () => {
    setErrorDialogOpen(false)
  }

  const [viewApprovalNotesDialogOpen, setViewApprovalNotesDialogOpen] = useState(false)

  const handleOpenViewApprovalNotesDialog = (application: Application) => {
    setSelectedApplication(application)
    setViewApprovalNotesDialogOpen(true)
  }

  const handleCloseViewApprovalNotesDialog = () => {
    setViewApprovalNotesDialogOpen(false)
  }

  const ErrorDialog = () => (
    <Dialog open={errorDialogOpen} onClose={handleCloseErrorDialog} fullScreen={isMobile}>
      <DialogTitle>Error</DialogTitle>
      <DialogContent>
        <Typography>
          There was an error loading the document. Please try again or submit a support request.
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button href='/support-request'>Submit Support Request</Button>
        <Button onClick={handleCloseErrorDialog}>Close</Button>
      </DialogActions>
    </Dialog>
  )

  const [isAssociateVehiclesDialogOpen, setAssociateVehiclesDialogOpen] = useState(false)

  const handleOpenAssociateVehiclesDialog = (application: Application) => {
    setSelectedApplication(application)
    setAssociateVehiclesDialogOpen(true)
  }

  const handleCloseAssociateVehiclesDialog = () => {
    setAssociateVehiclesDialogOpen(false)
  }

  const [viewDocumentsDialog, setViewDocumentsDialog] = useState(false)

  const handleOpenViewDocumentsDialog = (application: Application) => {
    setSelectedApplication(application)

    setViewDocumentsDialog(true)
  }

  const handleCloseViewDocumentsDialog = () => {
    setViewDocumentsDialog(false)
  }

  if (applicationsIsLoading) {
    return <FreeTechLogoLoading />
  }

  if (!hasViewPermission) {
    return (
      <PermissionDenied
        category='applications'
        action='view'
        description='You need permission to view applications.'
      />
    )
  }

  return (
    <>
      <ViewApprovalTerms
        application={selectedApplication}
        isOpen={viewApprovalNotesDialogOpen}
        onClose={handleCloseViewApprovalNotesDialog}
      />

      <AssignAgentDialog isOpen={isAssignAgentDialogOpen} onClose={() => setAssignAgentDialog(false)} />

      <AddNotesDialog
        application={selectedApplication}
        isOpen={isAddNotesDialogOpen}
        onClose={handleCloseAddNotesDialog}
      />

      <AssociateVehiclesDialog
        application={selectedApplication}
        isOpen={isAssociateVehiclesDialogOpen}
        onClose={handleCloseAssociateVehiclesDialog}
      />

      <ViewDocumentsDialog
        application={selectedApplication}
        isOpen={viewDocumentsDialog}
        onClose={handleCloseViewDocumentsDialog}
      />

      <ErrorDialog />
      <LoadingDialog />

      <AdminPageFrame
        title='Application Management'
        subtitle='Manage applications and their statuses.'
      >
      <Box
        display='flex'
        flexDirection='column'
        gap={2}
        sx={{ overflowX: 'hidden', maxWidth: '100%' }}
      >
        <ApplicationStatusTimeline
          role='admin'
          includeCompleted
          selectedFilter={statusFilter}
          onSelect={setStatusFilter}
        />
        <Box
          sx={
            isMobile
              ? {
                  position: 'sticky',
                  top: 0,
                  zIndex: 2,
                  bgcolor: 'background.default',
                  pt: 0.5,
                  pb: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 1.5,
                  width: '100%',
                  maxWidth: '100%',
                  '& .MuiFormControl-root, & .MuiTextField-root': { width: '100%', maxWidth: '100%' },
                }
              : undefined
          }
        >
          <FilterToolbar>
            <TextField
              label='Keyword Search (name, bank, email, notes, etc...)'
              variant='outlined'
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              sx={{ flex: 1, minWidth: { xs: '100%', sm: 280 } }}
            />
          </FilterToolbar>
        </Box>
      </Box>

      {isMobile ? (
        <MobileRecordList
          isEmpty={filteredApps.length === 0}
          empty='No applications match this filter.'
        >
          {filteredApps.map((application: Application) => (
            <ApplicationCard
              key={application.id}
              application={application}
              statusFilter={statusFilter}
              users={authUsers?.users || []}
              onViewDocument={handleViewDocument}
              onAssignAgent={openAssignAgentDialog}
              onMarkCompleted={markApplicationCompleted}
              onAddNotes={handleOpenAddNotesDialog}
              onViewDocuments={handleOpenViewDocumentsDialog}
              onViewApprovalNotes={handleOpenViewApprovalNotesDialog}
              onAssociateVehicles={handleOpenAssociateVehiclesDialog}
            />
          ))}
        </MobileRecordList>
      ) : (
        <Grid container spacing={2}>
          {filteredApps.map((application: Application) => (
            <Grid item lg={4} md={4} sm={6} xs={12} key={application.id}>
              <ApplicationCard
                application={application}
                statusFilter={statusFilter}
                users={authUsers?.users || []}
                onViewDocument={handleViewDocument}
                onAssignAgent={openAssignAgentDialog}
                onMarkCompleted={markApplicationCompleted}
                onAddNotes={handleOpenAddNotesDialog}
                onViewDocuments={handleOpenViewDocumentsDialog}
                onViewApprovalNotes={handleOpenViewApprovalNotesDialog}
                onAssociateVehicles={handleOpenAssociateVehiclesDialog}
              />
            </Grid>
          ))}
        </Grid>
      )}

      <AdminPandadocDialog
        open={isDialogOpen}
        onClose={handleCloseDialog}
        iframeUrl={documentUrl || ''}
        loading={loadingDialogOpen}
        onFormCompleted={() => {}}
        status={selectedApplication?.status}
        onIframeLoad={() => setLoadingDialogOpen(false)}
        applicationId={selectedApplication?.id}
      />
      </AdminPageFrame>
    </>
  )
}

export default ApplicationManagement
